import React, { useEffect, useState } from 'react';
import { Badge, Card, Button, Space, Tag, Table, message, Typography, Select, Tabs, Descriptions, Empty, Avatar, Divider, Tooltip, Input, Modal, Alert, Row, Col, Progress } from 'antd';
import { InfoCircleOutlined, TeamOutlined, TrophyOutlined, EnvironmentOutlined, PhoneOutlined, MailOutlined, GlobalOutlined, CalendarOutlined, EditOutlined, SearchOutlined, UserOutlined, UploadOutlined, CreditCardOutlined, CheckCircleOutlined, CloseCircleOutlined, UserAddOutlined, UsergroupAddOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import UserService from '@/services/user.service';
import { logger } from '@/utils/logger';
import { formatDate } from '@/utils/date';
import AddParticipantModal from './AddParticipantModal';
import { EditParticipantModal } from './EditParticipantModal';
import EditClubModal from './EditClubModal';
import { CreateCoachModal } from '../admin/CreateCoachModal';
import ParticipantStatistics from '../profile/ParticipantStatistics';
import BulkImportModal from './BulkImportModal';
import InviteManagerModal from '../admin/InviteManagerModal';
import type { ClubManagementProps } from '@/types/ui';
import type { ClubParticipant } from '@/types/api.types';
import { useAuth } from '@/contexts/AuthContext';
import ClubService from '@/services/club.service';
import FederationService from '@/services/federation.service';
import CategoryService from '@/services/category.service';
import FederationPopover from '../federation/FederationPopover';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { getUploadsBaseUrl } from '@/config/api.config';
import type { ApiResponse } from '@/types/api';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { Option } = Select;
// Не используем TabPane, так как он устарел

export const ClubManagement: React.FC<ClubManagementProps> = ({ clubId: propClubId }) => {
  const { user } = useAuth();
  const { t } = useTranslation(['club', 'common', 'subscription']);
  const navigate = useNavigate();
  const [participants, setParticipants] = useState<ClubParticipant[]>([]);
  const [coaches, setCoaches] = useState<{[key: number]: {fullName: string, id: number}}>({});
  const [loadingCoaches, setLoadingCoaches] = useState(false);
  // Состояния для тренеров
  const [coachesList, setCoachesList] = useState<any[]>([]);
  const [loadingCoachesList, setLoadingCoachesList] = useState(false);
  const [filteredCoaches, setFilteredCoaches] = useState<any[]>([]);
  const [coachSearchText, setCoachSearchText] = useState('');
  const [coachStatusFilter, setCoachStatusFilter] = useState<string | null>(null);
  const [isAddCoachModalVisible, setIsAddCoachModalVisible] = useState(false);
  const [isEditCoachModalVisible, setIsEditCoachModalVisible] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<any>(null);

  const [loading, setLoading] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isBulkImportModalVisible, setIsBulkImportModalVisible] = useState(false);
  const [editParticipantModalVisible, setEditParticipantModalVisible] = useState(false);
  const [editClubModalVisible, setEditClubModalVisible] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<ClubParticipant | null>(null);
  const [isStatisticsModalVisible, setIsStatisticsModalVisible] = useState(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState<number | null>(null);
  const [clubInfo, setClubInfo] = useState<{ id: number; name: string; [key: string]: any } | null>(null);
  const [loadingClub, setLoadingClub] = useState(true);
  const [availableClubs, setAvailableClubs] = useState<{ id: number; name: string }[]>([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [federation, setFederation] = useState<any>(null);
  // Добавляем состояние для фильтрации
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null); // null означает отсутствие фильтрации
  const [categories, setCategories] = useState<{id: number, name: string}[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [filteredParticipants, setFilteredParticipants] = useState<ClubParticipant[]>([]);
  // Состояние для подписок
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [extendedLicenseInfo, setExtendedLicenseInfo] = useState<{ hasExtendedLicense: boolean; extendedLicensePrice: number; canCreateTournament: boolean } | null>(null);
  const [extendedLicenseLoading, setExtendedLicenseLoading] = useState(false);
  // Приглашения менеджеров
  const [invitations, setInvitations] = useState<any[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [isInviteManagerModalVisible, setIsInviteManagerModalVisible] = useState(false);
  const [clubManagers, setClubManagers] = useState<any[]>([]);
  const [clubManagersLoading, setClubManagersLoading] = useState(false);
  const [pendingUsers, setPendingUsers] = useState<any[]>([]);
  const [pendingUsersLoading, setPendingUsersLoading] = useState(false);

  // Добавляем отладочные сообщения
  useEffect(() => {
    logger.debug(`Participants state: ${participants.length} items`);
    logger.debug(`FilteredParticipants state: ${filteredParticipants.length} items`);
  }, [participants, filteredParticipants]);

  // Проверка авторизации теперь выполняется в ProtectedRoute
  // Токены хранятся в httpOnly cookies, поэтому проверка localStorage не нужна

  const getBeltColor = (beltLevel: string | undefined) => {
    if (!beltLevel) return '#d9d9d9'; // серый для неизвестного пояса

    const beltColors: Record<string, string> = {
      'white': '#f0f0f0',
      'yellow': '#ffeb3b',
      'orange': '#ff9800',
      'green': '#4caf50',
      'blue': '#2196f3',
      'brown': '#795548',
      'black': '#000000',
      'red': '#f44336'
    };

    return beltColors[beltLevel.toLowerCase()] || '#d9d9d9';
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#f56a00', '#7265e6'];
    let sum = 0;
    for (let i = 0; i < name.length; i++) {
      sum += name.charCodeAt(i);
    }
    return colors[sum % colors.length];
  };

  const getAgeFromDateOfBirth = (dateOfBirth: string | undefined) => {
    if (!dateOfBirth) return 'Не указан';
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return `${age} лет`;
  };

  const columns: ColumnsType<ClubParticipant> = [
    {
      title: 'Спортсмен',
      key: 'fullName',
      render: (_, record) => {
        if (!record.participant) return 'Нет данных';
        const { firstName, lastName, middleName, dateOfBirth, gender } = record.participant;
        const fullName = `${lastName} ${firstName}${middleName ? ` ${middleName}` : ''}`;
        const avatarColor = getAvatarColor(fullName);
        const initials = `${lastName.charAt(0)}${firstName.charAt(0)}`;

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              style={{ backgroundColor: avatarColor, marginRight: 12 }}
              size="large"
            >
              {initials}
            </Avatar>
            <div>
              <div 
                style={{ 
                  fontWeight: 'bold', 
                  cursor: 'pointer',
                  color: '#1890ff',
                  textDecoration: 'underline'
                }}
                onClick={() => {
                  if (record.participant?.id) {
                    setSelectedParticipantId(record.participant.id);
                    setIsStatisticsModalVisible(true);
                  }
                }}
              >
                {fullName}
              </div>
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                {gender === 'male' ? '♂' : gender === 'female' ? '♀' : ''} {getAgeFromDateOfBirth(dateOfBirth)}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Характеристики',
      key: 'characteristics',
      render: (_, record) => {
        if (!record.participant) return 'Нет данных';
        const { weight, beltLevel, categoryId } = record.participant;
        const beltColor = getBeltColor(beltLevel);

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Tag color={beltColor} style={{ color: beltColor === '#000000' ? '#fff' : '#000' }}>
                {beltLevel || 'Пояс не указан'}
              </Tag>
            </div>
            <div>
              <Tooltip title="Вес">
                <Tag icon={<TrophyOutlined />} color="blue">{weight || 0} кг</Tag>
              </Tooltip>
              {categoryId && (
                <Tooltip title="Категория">
                  <Tag color="cyan">
                    {categories.find(c => c.id === categoryId)?.name || `Категория ${categoryId}`}
                  </Tag>
                </Tooltip>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Тренер',
      key: 'coach',
      render: (_, record) => {
        if (!record.participant) return 'Нет данных';
        if (!record.participant.coachId) return <Tag color="orange">Не назначен</Tag>;

        const coachId = record.participant.coachId;
        const coach = coaches[coachId];

        // Добавляем отладочный вывод для диагностики проблем с отображением тренера
        if (!coach && coachId) {
        }

        // Проверяем, есть ли у участника связь с объектом тренера
        const hasCoach = !!coach;

        // Формируем текст для подсказки
        const tooltipTitle = hasCoach
          ? `Тренер: ${coach.fullName} (ID: ${coachId})`
          : `Тренер с ID ${coachId} не найден в списке тренеров клуба`;

        return (
          <Space>
            <Tooltip title={tooltipTitle}>
              <Tag color={hasCoach ? "green" : "gold"} icon={<TeamOutlined />}>
                {hasCoach ? coach.fullName : `Тренер ID: ${coachId}`}
              </Tag>
            </Tooltip>
            {!hasCoach && (
              <Tooltip title="Обновить информацию о тренерах">
                <Button
                  type="link"
                  size="small"
                  icon={<InfoCircleOutlined />}
                  loading={loadingCoaches}
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchCoaches();
                  }}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Статус',
      key: 'status',
      width: 120,
      render: (_, record) => {
        if (!record.participant) {
          return <Tag color="gray">Нет данных</Tag>;
        }
        return (
          <Tag
            color={record.participant.status === 'active' ? 'green' : 'red'}
            style={{ fontWeight: 'bold' }}
          >
            {record.participant.status === 'active' ? 'Активен' : 'Неактивен'}
          </Tag>
        );
      },
    },
    {
      title: 'Дата добавления',
      key: 'createdAt',
      width: 150,
      render: (_, record) => {
        const date = new Date(record.createdAt);
        return (
          <Tooltip title={formatDate(date)}>
            <div style={{ whiteSpace: 'nowrap' }}>
              <CalendarOutlined style={{ marginRight: 5 }} />
              {formatDate(date)}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEditParticipant(record)}
          disabled={!record.participant}
        >
          Изменить
        </Button>
      ),
    },
  ];

  // Загрузка списка доступных клубов для админа
  const fetchAvailableClubs = async () => {
    if (user?.role !== 'admin') return;

    try {
      setLoadingClubs(true);
      const response = await ClubService.getClubs();

      // Нормализуем ответ: поддерживаем оба формата (data как массив или data.rows)
      const clubsArray = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.rows || []);

      // Проверяем, что ответ содержит данные клубов
      if (!response || !clubsArray || clubsArray.length === 0) {
        if (!response || !clubsArray) {
          logger.error('Invalid response format from getClubs:', response);
          message.error('Некорректный формат ответа от сервера');
        } else {
          logger.warn('No clubs found in the response');
          message.warning('Не найдено ни одного клуба');
        }
        setAvailableClubs([]);
        return;
      }

      // Преобразуем список клубов
      const clubs = clubsArray.map(club => ({
        id: club.id,
        name: club.name || `Клуб ${club.id}`
      }));

      setAvailableClubs(clubs);
    } catch (error) {
      logger.error('Error fetching available clubs:', error);
      message.error('Не удалось загрузить список клубов');
      setAvailableClubs([]);
    } finally {
      setLoadingClubs(false);
    }
  };

  // Загрузка информации о федерации
  const fetchFederationInfo = async (federationId: number) => {
    if (!federationId) return;

    try {
      const federationData = await FederationService.getFederation(federationId);
      setFederation(federationData);
    } catch (error) {
      logger.error('Error fetching federation info:', error);
      message.error('Не удалось загрузить информацию о федерации');
    }
  };

  // Функция для загрузки подписок клуба
  const fetchClubSubscriptions = async () => {
    try {
      setLoadingSubscriptions(true);
      const response = await apiService.get<ApiResponse<any[]>>(
        API_ENDPOINTS.SUBSCRIPTIONS.MY_CLUB
      );
      
      // Нормализуем данные подписок
      const subscriptionsData = Array.isArray(response.data?.data) ? response.data.data : [];
      
      // Обрабатываем каждую подписку: парсим features из JSON строки, если нужно
      const normalizedSubscriptions = subscriptionsData
        .filter((sub: any) => sub && typeof sub === 'object' && sub.id !== undefined && sub.id !== null)
        .map((sub: any) => {
          try {
            // Обрабатываем subscriptionPlan, если он есть
            if (sub.subscriptionPlan && typeof sub.subscriptionPlan === 'object') {
              // Если features - строка, парсим её
              if (typeof sub.subscriptionPlan.features === 'string' && sub.subscriptionPlan.features.trim()) {
                try {
                  const parsed = JSON.parse(sub.subscriptionPlan.features);
                  sub.subscriptionPlan.features = Array.isArray(parsed) ? parsed : null;
                } catch (e) {
                  logger.warn('Failed to parse subscription plan features:', e);
                  sub.subscriptionPlan.features = null;
                }
              } else if (!Array.isArray(sub.subscriptionPlan.features) && sub.subscriptionPlan.features !== null) {
                sub.subscriptionPlan.features = null;
              }
              
              // Убеждаемся, что durationDays может быть null
              if (sub.subscriptionPlan.durationDays === undefined) {
                sub.subscriptionPlan.durationDays = null;
              }
              
              // Убеждаемся, что maxTournaments и maxParticipants могут быть null
              if (sub.subscriptionPlan.maxTournaments === undefined) {
                sub.subscriptionPlan.maxTournaments = null;
              }
              if (sub.subscriptionPlan.maxParticipants === undefined) {
                sub.subscriptionPlan.maxParticipants = null;
              }
            }
            
            // Убеждаемся, что обязательные поля присутствуют (включая лимиты из БД)
            return {
              id: Number(sub.id) || 0,
              clubId: Number(sub.clubId) || 0,
              subscriptionPlanId: sub.subscriptionPlanId !== undefined && sub.subscriptionPlanId !== null ? Number(sub.subscriptionPlanId) : null,
              status: (['active', 'cancelled', 'expired', 'pending'].includes(sub.status) ? sub.status : 'pending') as 'active' | 'cancelled' | 'expired' | 'pending',
              price: Number(sub.price) || 0,
              startDate: sub.startDate ? String(sub.startDate) : new Date().toISOString(),
              endDate: sub.endDate ? String(sub.endDate) : null,
              autoRenew: Boolean(sub.autoRenew ?? false),
              usedTournaments: Number(sub.usedTournaments) || 0,
              usedParticipants: Number(sub.usedParticipants) || 0,
              maxTournamentsOverride: sub.maxTournamentsOverride != null && sub.maxTournamentsOverride !== '' ? Number(sub.maxTournamentsOverride) : null,
              maxParticipantsOverride: sub.maxParticipantsOverride != null && sub.maxParticipantsOverride !== '' ? Number(sub.maxParticipantsOverride) : null,
              paymentMethod: sub.paymentMethod ? String(sub.paymentMethod) : null,
              notes: sub.notes ? String(sub.notes) : null,
              createdAt: sub.createdAt ? String(sub.createdAt) : new Date().toISOString(),
              updatedAt: sub.updatedAt ? String(sub.updatedAt) : new Date().toISOString(),
              subscriptionPlan: sub.subscriptionPlan && typeof sub.subscriptionPlan === 'object' ? {
                id: Number(sub.subscriptionPlan.id) || 0,
                name: String(sub.subscriptionPlan.name || 'Не указан'),
                description: sub.subscriptionPlan.description ? String(sub.subscriptionPlan.description) : null,
                price: Number(sub.subscriptionPlan.price) || 0,
                durationDays: sub.subscriptionPlan.durationDays !== undefined && sub.subscriptionPlan.durationDays !== null ? Number(sub.subscriptionPlan.durationDays) : null,
                maxTournaments: sub.subscriptionPlan.maxTournaments !== undefined && sub.subscriptionPlan.maxTournaments !== null ? Number(sub.subscriptionPlan.maxTournaments) : null,
                maxParticipants: sub.subscriptionPlan.maxParticipants !== undefined && sub.subscriptionPlan.maxParticipants !== null ? Number(sub.subscriptionPlan.maxParticipants) : null,
                features: Array.isArray(sub.subscriptionPlan.features) 
                  ? sub.subscriptionPlan.features.map((f: any) => String(f || '')).filter(Boolean)
                  : null
              } : undefined
            };
          } catch (e) {
            logger.error('Error normalizing subscription:', e, sub);
            return null;
          }
        })
        .filter((sub: any): sub is any => {
          return sub !== null && 
                 sub !== undefined && 
                 typeof sub === 'object' && 
                 typeof sub.id === 'number' && 
                 sub.id > 0 && 
                 typeof sub.status === 'string';
        });
      
      setSubscriptions(normalizedSubscriptions);
      logger.info(`Loaded ${normalizedSubscriptions.length} subscriptions`);
    } catch (error: any) {
      logger.error('Error fetching club subscriptions:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при загрузке подписок';
      message.error(errorMessage);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const fetchExtendedLicenseInfo = async () => {
    try {
      setExtendedLicenseLoading(true);
      const res = await apiService.get<ApiResponse<{ hasExtendedLicense: boolean; extendedLicensePrice: number; canCreateTournament: boolean }>>(
        API_ENDPOINTS.PAYMENTS.EXTENDED_LICENSE_INFO
      );
      const data = res.data?.data;
      if (data) setExtendedLicenseInfo(data);
    } catch (e) {
      logger.error('Error fetching extended license info:', e);
    } finally {
      setExtendedLicenseLoading(false);
    }
  };

  const fetchInvitations = async (clubId: number) => {
    try {
      setInvitationsLoading(true);
      const res = await apiService.get(API_ENDPOINTS.CLUB_MANAGERS.INVITATIONS_BY_CLUB(clubId));
      setInvitations((res as any)?.data?.data ?? []);
    } catch (e) {
      logger.error('Error fetching invitations:', e);
      setInvitations([]);
    } finally {
      setInvitationsLoading(false);
    }
  };

  const handleCancelInvitation = async (id: number, cId: number) => {
    try {
      await apiService.delete(API_ENDPOINTS.CLUB_MANAGERS.CANCEL_INVITATION(id, cId));
      message.success('Приглашение отменено');
      if (clubInfo?.id) fetchInvitations(clubInfo.id);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ошибка при отмене');
    }
  };

  const fetchClubManagers = async (clubId: number) => {
    try {
      setClubManagersLoading(true);
      const res = await apiService.get(API_ENDPOINTS.CLUB_MANAGERS.BY_CLUB(clubId));
      setClubManagers((res as any)?.data?.data ?? []);
    } catch (e) {
      logger.error('Error fetching club managers:', e);
      setClubManagers([]);
    } finally {
      setClubManagersLoading(false);
    }
  };

  const fetchPendingUsers = async (clubId: number) => {
    try {
      setPendingUsersLoading(true);
      const res = await apiService.get(API_ENDPOINTS.CLUB_MANAGERS.PENDING(clubId));
      setPendingUsers((res as any)?.data?.data ?? []);
    } catch (e) {
      logger.error('Error fetching pending users:', e);
      setPendingUsers([]);
    } finally {
      setPendingUsersLoading(false);
    }
  };

  const handleApprovePending = async (userId: number, cId: number) => {
    try {
      await apiService.post(API_ENDPOINTS.CLUB_MANAGERS.APPROVE_PENDING(userId, cId));
      message.success('Пользователь подтверждён как менеджер');
      if (clubInfo?.id) {
        fetchPendingUsers(clubInfo.id);
        fetchClubManagers(clubInfo.id);
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ошибка при подтверждении');
    }
  };

  // Обработчик выбора клуба
  const handleClubChange = async (clubId: number | string) => {
    // Преобразуем в число, если пришла строка
    const id = typeof clubId === 'string' ? parseInt(clubId, 10) : clubId;
    try {
      setLoadingClub(true);
      const club = await ClubService.getClubById(id);
      // Сохраняем все поля клуба для отображения на вкладке информации
      setClubInfo(club);

      // Если у клуба есть федерация, загружаем информацию о ней
      if (club.federationId) {
        fetchFederationInfo(club.federationId);
      } else if (club.federation?.id) {
        fetchFederationInfo(club.federation.id);
      } else {
        setFederation(null);
      }

      // Загружаем подписки, приглашения и менеджеров клуба
      if (club.id) {
        fetchClubSubscriptions();
        fetchExtendedLicenseInfo();
        fetchInvitations(club.id);
        fetchClubManagers(club.id);
        fetchPendingUsers(club.id);
      }
    } catch (error) {
      logger.error('Error changing club:', error);
      message.error('Не удалось загрузить информацию о клубе');
    } finally {
      setLoadingClub(false);
    }
  };

  // Загружаем подписки и приглашения при изменении информации о клубе (не для ожидающих подтверждения)
  useEffect(() => {
    if (clubInfo && clubInfo.id && user && !(user.clubId && (user as any).managerApprovalStatus === 'pending')) {
      fetchClubSubscriptions();
      fetchExtendedLicenseInfo();
      fetchInvitations(clubInfo.id);
      fetchClubManagers(clubInfo.id);
      fetchPendingUsers(clubInfo.id);
    }
  }, [clubInfo?.id, user]);

  // Получаем информацию о клубе пользователя
  const fetchUserClub = async () => {
    try {
      setLoadingClub(true);

      // Проверяем, что пользователь авторизован
      // Токены теперь в httpOnly cookies, проверка через user из AuthContext
      if (!user) {
        logger.warn('User not authenticated');
        return;
      }


      // Если передан clubId через props, используем его
      if (propClubId) {
        // Получаем информацию о клубе по ID
        const club = await ClubService.getClubById(propClubId);
        // Сохраняем все поля клуба для отображения на вкладке информации
        setClubInfo(club);

        // Если у клуба есть федерация, загружаем информацию о ней
        if (club.federationId) {
          fetchFederationInfo(club.federationId);
        } else if (club.federation?.id) {
          fetchFederationInfo(club.federation.id);
        }
        return;
      }

      // Если пользователь - менеджер, получаем его клуб
      if (user && (user.role === 'manager')) {
        try {
          // Пропускаем попытку получить клуб менеджера через API, так как она вызывает ошибку 500

          // Если у пользователя есть clubId в контексте авторизации
          if (user.clubId) {
            // Используем clubId из контекста авторизации
            try {
              const club = await ClubService.getClubById(user.clubId);
              // Сохраняем все поля клуба для отображения на вкладке информации
              setClubInfo(club);

              // Если у клуба есть федерация, загружаем информацию о ней
              if (club.federationId) {
                fetchFederationInfo(club.federationId);
              } else if (club.federation?.id) {
                fetchFederationInfo(club.federation.id);
              }
              return; // Если успешно, выходим
            } catch (clubError) {
              logger.error('Error fetching club by ID from auth context:', clubError);
            }
          }

          // Метод 2: получаем актуальную информацию о пользователе и используем clubId из нее
          try {
            // Получаем актуальную информацию о пользователе
            const currentUser = await UserService.getCurrentUser();

            if (currentUser.clubId) {
              try {
                const club = await ClubService.getClubById(currentUser.clubId);
                // Сохраняем все поля клуба для отображения на вкладке информации
                setClubInfo(club);
                return; // Если успешно, выходим
              } catch (clubError) {
                logger.error('Error fetching club by ID:', clubError);
              }
            } else {
              logger.warn('Current user does not have clubId');
            }
          } catch (userError) {
            logger.error('Error fetching current user:', userError);
          }

          // Метод 3: Используем clubId из контекста авторизации
          if (user.clubId) {
            try {
              const club = await ClubService.getClubById(user.clubId);
              setClubInfo({
                id: club.id,
                name: club.name
              });
              return; // Если успешно, выходим
            } catch (clubError) {
              logger.error('Error fetching club by ID from auth context:', clubError);
            }
          }

          // Метод 4: получаем список клубов и ищем клуб менеджера
          try {
            // Получаем список клубов
            const response = await ClubService.getClubs();

            // Нормализуем ответ: поддерживаем оба формата
            const clubsArray = Array.isArray(response.data) 
              ? response.data 
              : (response.data?.rows || []);
            
            if (clubsArray.length > 0) {
              // Для менеджера берем первый клуб из списка
              const firstClub = clubsArray[0];

              // Проверяем, что клуб существует и имеет все необходимые поля
              if (firstClub && firstClub.id) {
                setClubInfo({
                  id: firstClub.id,
                  name: firstClub.name || `Клуб ${firstClub.id}`
                });

                // Показываем уведомление, что используется первый клуб из списка
                message.info(`Используется первый клуб из списка: ${firstClub.name}`);
                return; // Если успешно, выходим
              } else {
                logger.warn('First club in list has invalid format:', firstClub);
              }
            } else {
              logger.warn('No clubs found in the response');
            }
          } catch (clubsError) {
            logger.error('Error fetching clubs list:', clubsError);
          }

          // Если все методы не сработали
          message.error('Не удалось получить информацию о клубе менеджера');
        } catch (error) {
          logger.error('Error in manager club flow:', error);
          message.error('Не удалось получить информацию о клубе менеджера');
        }
      } else if (user && user.role === 'admin') {
        // Для админа загрузим список клубов
        await fetchAvailableClubs();

        // Загрузим первый клуб из списка для админа
        try {
          const response = await ClubService.getClubs();
          // Нормализуем ответ: поддерживаем оба формата
          const clubsArray = Array.isArray(response.data) 
            ? response.data 
            : (response.data?.rows || []);
          
          if (clubsArray.length > 0) {
            const firstClub = clubsArray[0];
            // Получаем полную информацию о клубе
            const club = await ClubService.getClubById(firstClub.id);
            setClubInfo(club);

            // Если у клуба есть федерация, загружаем информацию о ней
            if (club.federationId) {
              fetchFederationInfo(club.federationId);
            } else if (club.federation?.id) {
              fetchFederationInfo(club.federation.id);
            }
          }
        } catch (error) {
          logger.error('Error fetching clubs for admin:', error);
        }
      }
    } catch (error) {
      logger.error('Error fetching club info:', error);
      message.error('Не удалось получить информацию о клубе');
    } finally {
      setLoadingClub(false);
    }
  };

  // Получение информации о тренерах
  const fetchCoaches = async () => {
    try {
      setLoadingCoaches(true);
      const { data } = await UserService.getClubCoaches(clubInfo?.id || 0);

      if (data && Array.isArray(data)) {
        // Преобразуем массив тренеров в объект для быстрого доступа по ID
        const coachesMap = data.reduce((acc, coach) => {
          // Формируем полное имя тренера, если оно не указано явно
          if (!coach.fullName) {
            // Проверяем, есть ли у объекта coach свойства firstName и lastName
            if (coach.user && coach.user.firstName && coach.user.lastName) {
              // Если данные находятся в свойстве user
              coach.fullName = `${coach.user.lastName || ''} ${coach.user.firstName || ''}${coach.user.middleName ? ` ${coach.user.middleName}` : ''}`;
            } else if ('firstName' in coach && 'lastName' in coach) {
              // Если данные находятся напрямую в объекте coach
              const firstName = (coach as any).firstName || '';
              const lastName = (coach as any).lastName || '';
              const middleName = (coach as any).middleName || '';
              coach.fullName = `${lastName} ${firstName}${middleName ? ` ${middleName}` : ''}`;
            } else {
              // Если нет данных для формирования полного имени
              (coach as any).fullName = `Тренер ID: ${(coach as any).id}`;
            }
          }
          acc[coach.id] = coach;
          return acc;
        }, {} as {[key: number]: any});

        setCoaches(coachesMap);
      }
    } catch (error) {
      logger.error('Error fetching coaches:', error);
      message.error('Не удалось загрузить информацию о тренерах');
    } finally {
      setLoadingCoaches(false);
    }
  };

  // Получение списка тренеров для вкладки "Тренеры"
  const fetchCoachesList = async () => {
    if (!clubInfo?.id) {
      logger.warn('Cannot fetch coaches: clubInfo.id is not defined');
      return;
    }
    try {
      setLoadingCoachesList(true);
      const response = await UserService.getClubCoaches(clubInfo.id);
      
      if (response.data && Array.isArray(response.data)) {
        // Форматируем имена тренеров в формате "Фамилия Имя Отчество"
        const formattedCoaches = response.data.map((coach: any) => {
          const lastName = coach.user?.lastName || coach.lastName || '';
          const firstName = coach.user?.firstName || coach.firstName || '';
          const middleName = coach.user?.middleName || coach.middleName || '';
          
          let fullName = '';
          if (lastName && firstName) {
            // Формат: Фамилия Имя Отчество
            fullName = `${lastName} ${firstName}${middleName ? ` ${middleName}` : ''}`.trim();
          } else if (coach.fullName) {
            // Если есть готовое полное имя, используем его
            fullName = coach.fullName;
          } else {
            fullName = `Тренер ID: ${coach.id}`;
          }
          
          return {
            ...coach,
            fullName,
            firstName: coach.user?.firstName || coach.firstName,
            lastName: coach.user?.lastName || coach.lastName,
            middleName: coach.user?.middleName || coach.middleName,
            email: coach.user?.email || coach.email,
            phone: coach.user?.phoneNumber || coach.phoneNumber || coach.phone,
            phoneNumber: coach.user?.phoneNumber || coach.phoneNumber || coach.phone,
            status: coach.user?.status || coach.status,
            qualification: coach.qualification || 'Не указана',
            experience: coach.experience || 0,
            createdAt: coach.user?.createdAt || coach.createdAt,
            clubId: coach.clubId || coach.club?.id || clubInfo?.id
          };
        });
        
        setCoachesList(formattedCoaches);
      }
    } catch (error) {
      logger.error('Error fetching coaches list:', error);
      message.error('Не удалось загрузить список тренеров');
      setCoachesList([]);
    } finally {
      setLoadingCoachesList(false);
    }
  };

  // Функция для фильтрации тренеров
  const filterCoaches = () => {
    if (!coachesList || coachesList.length === 0) {
      setFilteredCoaches([]);
      return;
    }

    let filtered = [...coachesList];

    // Фильтрация по тексту поиска
    if (coachSearchText) {
      filtered = filtered.filter(coach => {
        // Используем формат "Фамилия Имя Отчество" для поиска
        const fullName = `${coach.lastName || ''} ${coach.firstName || ''} ${coach.middleName || ''}`.toLowerCase();
        return fullName.includes(coachSearchText.toLowerCase());
      });
    }

    // Фильтрация по статусу
    if (coachStatusFilter) {
      filtered = filtered.filter(coach => coach.status === coachStatusFilter);
    }

    setFilteredCoaches(filtered);
  };

  // Обработчики для тренеров
  const handleEditCoach = (coach: any) => {
    setSelectedCoach(coach);
    setIsEditCoachModalVisible(true);
  };

  const handleCoachStatusFilterChange = (value: string | null) => {
    setCoachStatusFilter(value);
  };

  // Альтернативный метод получения участников клуба
  const fetchClubParticipantsAlternative = async (clubId: number): Promise<ClubParticipant[]> => {
    try {
      // Получаем всех пользователей с ролью спортсмена
      const response = await UserService.getAllUsers({ role: 'participant' });

      if (!response.data || !Array.isArray(response.data.rows)) {
        logger.error('Invalid response format for users:', response);
        return [];
      }

      // Фильтруем пользователей по clubId
      const clubUsers = response.data.rows.filter(user => user.clubId === clubId);

      // Преобразуем в формат ClubParticipant, аналогично основному методу
      const result: ClubParticipant[] = clubUsers.map((user: any) => ({
        id: user.id, // Используем ID пользователя как ID записи
        participantId: user.id, // Используем ID пользователя как participantId
        clubId: clubId,
        status: (user.status as 'active' | 'inactive') || 'active',
        createdAt: user.createdAt || new Date().toISOString(),
        updatedAt: user.updatedAt || new Date().toISOString(),
        participant: {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          middleName: user.middleName || '',
          dateOfBirth: user.dateOfBirth || new Date().toISOString(),
          gender: (user.gender as 'male' | 'female') || 'male',
          weight: user.weight || 0,
          beltLevel: user.beltLevel || '',
          categoryId: user.categoryId || 0,
          clubId: user.clubId || 0,
          coachId: user.coachId || null, // Добавляем coachId из данных участника
          status: (user.status as 'active' | 'inactive') || 'active',
          createdAt: user.createdAt || new Date().toISOString(),
          updatedAt: user.updatedAt || new Date().toISOString()
        }
      }));

      return result;
    } catch (error) {
      logger.error('Error in alternative method for fetching club participants:', error);
      return [];
    }
  };

  const fetchParticipants = async () => {
    if (!clubInfo?.id) {
      logger.warn('Cannot fetch participants: clubInfo.id is not defined');
      return;
    }

    // Токены теперь в httpOnly cookies, они отправляются автоматически
    // Проверка авторизации выполняется через ProtectedRoute и AuthContext

    try {
      setLoading(true);

      // Проверяем существование клуба перед запросом участников
      try {
        // Проверяем, что клуб существует
        await ClubService.getClubById(clubInfo.id);
      } catch (clubError) {
        logger.error(`Club with ID ${clubInfo.id} not found:`, clubError);
        message.error(`Клуб с ID ${clubInfo.id} не найден`);
        setParticipants([]);
        return;
      }

      try {
        // Пытаемся получить участников через API
        const response = await UserService.getClubParticipants(clubInfo.id);

        // Проверяем формат ответа - может быть {data: {data: Array}} или {success: true, data: Array}
        let participantsData: any[] = [];

        if (response.data && Array.isArray(response.data)) {
          // Формат {data: Array}
          participantsData = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          // Формат {data: {data: Array}}
          participantsData = response.data.data;
        } else if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
          // Формат {data: {success: true, data: Array}}
          participantsData = response.data.data;
        } else {
          logger.error('Invalid response format for club participants:', response);
          message.error('Некорректный формат ответа от сервера');
          setParticipants([]);
          return;
        }

        // Теперь мы получаем данные напрямую из таблицы users
        try {
          const clubParticipants: ClubParticipant[] = participantsData.map((p: any): ClubParticipant => {
            // Создаем объект, соответствующий интерфейсу ClubParticipant
            const clubParticipant: ClubParticipant = {
              id: p.id, // Используем ID пользователя как ID записи
              participantId: p.id, // Используем ID пользователя как participantId
              clubId: p.clubId,
              status: p.status as 'active' | 'inactive',
              createdAt: p.createdAt,
              updatedAt: p.updatedAt,
              // Добавляем данные участника напрямую
              participant: {
                id: p.id,
                firstName: p.firstName,
                lastName: p.lastName,
                middleName: p.middleName || '',
                dateOfBirth: p.dateOfBirth,
                gender: p.gender as 'male' | 'female',
                weight: p.weight || 0,
                beltLevel: p.beltLevel || '',
                categoryId: p.categoryId || 0,
                clubId: p.clubId || 0,
                coachId: p.coachId || null, // Добавляем coachId из данных участника
                status: p.status as 'active' | 'inactive',
                createdAt: p.createdAt,
                updatedAt: p.updatedAt
              }
            };

            return clubParticipant;
          });

          setParticipants(clubParticipants);
        } catch (mappingError) {
          message.error('Ошибка при обработке данных участников');
          setParticipants([]);
        }
      } catch (participantsError: any) {
        const status = participantsError?.response?.status;

        // Если получили ошибку 500 с сообщением о несоответствии алиасов
        if (status === 500 &&
            (participantsError.response?.data?.error?.includes('alias') ||
             participantsError.message?.includes('alias'))) {

          logger.warn('Using alternative method to fetch club participants due to alias error');

          // Используем альтернативный метод получения участников
          const alternativeParticipants = await fetchClubParticipantsAlternative(clubInfo.id);
          // Явное приведение типов для устранения ошибки TypeScript
          setParticipants(alternativeParticipants as ClubParticipant[]);
          return;
        }

        // Проверяем код ошибки и логируем соответственно
        if (status === 404) {
          message.warning(`Спортсмены не найдены для клуба ${clubInfo.name || clubInfo.id}`);
        } else if (status === 403) {
          logger.warn(`Access denied for club ${clubInfo.id} participants`);
          message.error('У вас нет прав на просмотр спортсменов этого клуба');
        } else if (status === 429) {
          // Обработка ошибки rate limiting
          const retryAfter = participantsError.response?.data?.retryAfter;
          const errorMessage = participantsError.response?.data?.error || participantsError.response?.data?.message || 'Слишком много запросов';
          const retryMessage = retryAfter 
            ? `Попробуйте позже (после ${new Date(retryAfter * 1000).toLocaleTimeString()})` 
            : 'Попробуйте позже';
          
          logger.warn('Rate limit exceeded while fetching participants:', {
            clubId: clubInfo.id,
            retryAfter
          });
          message.warning(`${errorMessage}. ${retryMessage}`);
        } else {
          // Логируем только неожиданные ошибки как ERROR
          logger.error(`Error fetching participants for club ${clubInfo.id}:`, participantsError);
          message.error('Не удалось загрузить список спортсменов клуба');
        }

        setParticipants([]);
      }
    } catch (error: any) {
      // Обработка ошибки rate limiting (429) в основном catch блоке
      if (error?.response?.status === 429) {
        const retryAfter = error?.response?.data?.retryAfter;
        const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Слишком много запросов';
        const retryMessage = retryAfter 
          ? `Попробуйте позже (после ${new Date(retryAfter * 1000).toLocaleTimeString()})` 
          : 'Попробуйте позже';
        
        logger.warn('Rate limit exceeded while fetching participants:', {
          clubId: clubInfo?.id,
          retryAfter
        });
        message.warning(`${errorMessage}. ${retryMessage}`);
      } else {
        logger.error('Unexpected error in fetchParticipants:', error);
        message.error('Не удалось загрузить список спортсменов клуба');
      }
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditParticipant = (participant: ClubParticipant) => {
    if (!participant.participant) {
      message.error('Данные участника недоступны');
      return;
    }

    // Проверяем, есть ли у участника тренер и загружена ли информация о тренере
    if (participant.participant.coachId && !coaches[participant.participant.coachId]) {
      // Если информация о тренере не загружена, загружаем ее
      fetchCoaches();
    }

    setSelectedParticipant(participant);
    setEditParticipantModalVisible(true);
  };

  // Функция для фильтрации спортсменов
  const filterParticipants = () => {
    if (!participants || participants.length === 0) {
      setFilteredParticipants([]);
      return;
    }

    let filtered = [...participants];

    // Фильтрация по тексту поиска
    if (searchText) {
      filtered = filtered.filter(item => {
        const participant = item.participant;
        if (!participant) {
          return false;
        }

        const fullName = `${participant.firstName} ${participant.lastName} ${participant.middleName || ''}`.toLowerCase();
        return fullName.includes(searchText.toLowerCase());
      });
    }

    // Фильтрация по статусу
    // По умолчанию показываем только активных, скрываем неактивных
    // Если явно выбран фильтр "неактивные", показываем их
    if (statusFilter) {
      filtered = filtered.filter(item => {
        const participant = item.participant;
        if (!participant) {
          return false;
        }

        return participant.status === statusFilter;
      });
    } else {
      // По умолчанию фильтруем неактивных (показываем только активных)
      filtered = filtered.filter(item => {
        const participant = item.participant;
        if (!participant) {
          return false;
        }

        return participant.status === 'active';
      });
    }

    // Фильтрация по категории
    if (categoryFilter) {
      filtered = filtered.filter(item => {
        const participant = item.participant;
        if (!participant) {
          return false;
        }

        return participant.categoryId === categoryFilter;
      });
    }

    setFilteredParticipants(filtered);
  };

  // Обработчик изменения фильтра по статусу
  const handleStatusFilterChange = (value: string | null) => {
    setStatusFilter(value);
  };

  // Обработчик изменения фильтра по категории
  const handleCategoryFilterChange = (value: number | string | null) => {
    // Если значение пустая строка, устанавливаем null
    setCategoryFilter(value === '' ? null : Number(value));
  };

  // Колонки для таблицы тренеров
  const coachColumns: ColumnsType<any> = [
    {
      title: 'Тренер',
      key: 'fullName',
      render: (_, record) => {
        // Формат отображения: Фамилия Имя Отчество
        const lastName = record.lastName || '';
        const firstName = record.firstName || '';
        const middleName = record.middleName || '';
        const fullName = record.fullName || (lastName && firstName 
          ? `${lastName} ${firstName}${middleName ? ` ${middleName}` : ''}`.trim()
          : `Тренер ID: ${record.id}`);
        const avatarColor = getAvatarColor(fullName);
        const initials = `${(lastName || '').charAt(0)}${(firstName || '').charAt(0)}`;

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              style={{ backgroundColor: avatarColor, marginRight: 12 }}
              size="large"
              icon={<UserOutlined />}
            >
              {initials}
            </Avatar>
            <div>
              <div style={{ fontWeight: 'bold' }}>{fullName}</div>
              {record.email && (
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {record.email}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Квалификация',
      key: 'qualification',
      dataIndex: 'qualification',
      render: (qualification: string) => (
        <Tag color="blue">
          {qualification || 'Не указана'}
        </Tag>
      ),
    },
    {
      title: 'Опыт',
      key: 'experience',
      dataIndex: 'experience',
      render: (experience: number) => (
        <Tag color="cyan">
          {experience ? `${experience} лет` : 'Не указан'}
        </Tag>
      ),
    },
    {
      title: 'Статус',
      key: 'status',
      width: 120,
      render: (_, record) => {
        const status = record.status || 'active';
        const statusConfig: Record<string, { color: string; text: string }> = {
          active: { color: 'green', text: 'Активен' },
          inactive: { color: 'red', text: 'Неактивен' },
          pending: { color: 'orange', text: 'Ожидает' },
          suspended: { color: 'gray', text: 'Приостановлен' }
        };

        return (
          <Tag color={statusConfig[status]?.color || 'default'}>
            {statusConfig[status]?.text || status}
          </Tag>
        );
      },
    },
    {
      title: 'Дата добавления',
      key: 'createdAt',
      width: 150,
      render: (_, record) => {
        if (!record.createdAt) return '-';
        const date = new Date(record.createdAt);
        return (
          <Tooltip title={formatDate(date)}>
            <div style={{ whiteSpace: 'nowrap' }}>
              <CalendarOutlined style={{ marginRight: 5 }} />
              {formatDate(date)}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEditCoach(record)}
        >
          Изменить
        </Button>
      ),
    },
  ];

  // Обновляем filteredParticipants при изменении participants
  useEffect(() => {
    // Всегда применяем фильтрацию (по умолчанию скрываем неактивных)
    filterParticipants();
  }, [participants]); // eslint-disable-line react-hooks/exhaustive-deps

  // Вызываем функцию фильтрации при изменении текста поиска, фильтра по статусу или категории
  useEffect(() => {
    // Всегда применяем фильтрацию (по умолчанию скрываем неактивных)
    filterParticipants();
  }, [searchText, statusFilter, categoryFilter, categories]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUserClub();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Загрузка категорий для фильтрации участников
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);

      // Получаем категории из API
      const categoriesData = await CategoryService.getAll();

      // Форматируем категории для отображения
      const formattedCategories = categoriesData.map(category => ({
        id: category.id,
        name: `${category.name} (${category.weightFrom}-${category.weightTo} кг, ${category.ageFrom}-${category.ageTo} лет, ${category.gender === 'male' ? 'М' : category.gender === 'female' ? 'Ж' : 'М/Ж'})`
      }));

      setCategories(formattedCategories);
      } catch (error) {
      message.error('Не удалось загрузить категории');
    } finally {
      setLoadingCategories(false);
    }
  };

  const isPendingApproval = user?.clubId && (user as any).managerApprovalStatus === 'pending';

  useEffect(() => {
    if (clubInfo?.id && !isPendingApproval) {
      fetchParticipants();
      fetchCoaches();
      fetchCoachesList(); // Загружаем список тренеров для вкладки
      fetchCategories(); // Загружаем категории для фильтрации участников
    }
  }, [clubInfo?.id, isPendingApproval]); // eslint-disable-line react-hooks/exhaustive-deps

  // Обновляем filteredCoaches при изменении coachesList или фильтров
  useEffect(() => {
    if (coachSearchText || coachStatusFilter) {
      filterCoaches();
    } else {
      setFilteredCoaches(coachesList);
    }
  }, [coachesList, coachSearchText, coachStatusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Отображаем загрузку клуба
  if (loadingClub) {
    return (
      <Card loading={true}>
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          Загрузка информации о клубе...
        </div>
      </Card>
    );
  }

  // Если клуб не найден
  if (!clubInfo && user?.role === 'admin') {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Title level={4}>Выберите клуб для просмотра спортсменов</Title>

          {loadingClubs ? (
            <div>Загрузка списка клубов...</div>
          ) : (
            <Select
              style={{ width: 300, marginTop: 20 }}
              placeholder="Выберите клуб"
              onChange={handleClubChange}
              loading={loadingClubs}
            >
              {availableClubs.map(club => (
                <Option key={club.id} value={club.id}>{club.name}</Option>
              ))}
            </Select>
          )}
        </div>
      </Card>
    );
  }

  // Если клуб не найден для менеджера
  if (!clubInfo && (user?.role === 'manager')) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Title level={4}>У вас нет привязки к клубу</Title>
          <p>Обратитесь к администратору для назначения вас менеджером клуба</p>
        </div>
      </Card>
    );
  }

  const canInviteManager = (user?.role === 'admin') || ((user?.role === 'manager' || user?.role === 'coach' || user?.role === 'user') && clubInfo?.id === user?.clubId);

  // Стили для компонента
  const styles = {
    card: {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      borderRadius: '8px',
      overflow: 'hidden',
    },
    tabContent: {
      padding: '16px 0',
    },
    clubHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '24px',
    },
    avatar: {
      backgroundColor: '#1890ff',
      marginRight: '16px',
    },
    clubTitle: {
      margin: 0,
    },
    clubSubtitle: {
      color: '#8c8c8c',
    },
    divider: {
      margin: '24px 0 16px',
    },
    actionButton: {
      marginTop: '24px',
      textAlign: 'right',
    },
  };

  // Проверяем, что у нас есть вся необходимая информация о клубе
  if (clubInfo && (!clubInfo.id || clubInfo.id <= 0)) {
    return (
      <Card style={styles.card}>
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Title level={4}>Ошибка загрузки информации о клубе</Title>
          <p>Неверный идентификатор клуба: {clubInfo.id}</p>
          <Button type="primary" onClick={fetchUserClub} icon={<InfoCircleOutlined />}>Попробовать снова</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TrophyOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          <span>Управление клубом: {clubInfo?.name || ''}</span>
        </div>
      }
      extra={user?.role === 'admin' && (
        <Select
          style={{ width: 200 }}
          placeholder="Выберите клуб"
          onChange={handleClubChange}
          loading={loadingClubs}
          value={clubInfo?.id}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          optionFilterProp="children"
          showSearch
          filterOption={(input, option) =>
            (option?.children?.toString().toLowerCase().indexOf(input.toLowerCase()) ?? -1) >= 0
          }
          notFoundContent="Клубы не найдены"
        >
          {availableClubs.map(club => (
            <Option key={club.id} value={club.id}>{club.name || `Клуб ${club.id}`}</Option>
          ))}
        </Select>
      )}
      style={styles.card}
    >
      {user?.clubId && (user as any).managerApprovalStatus === 'pending' ? (
        <Alert
          message="Ожидание подтверждения"
          description="Вы зарегистрировались в клуб. Доступ к данным клуба будет предоставлен после подтверждения одним из менеджеров клуба."
          type="warning"
          showIcon
          style={{ marginTop: 24 }}
        />
      ) : (
      <Tabs
        defaultActiveKey="info"
        type="card"
        className="club-tabs"
        animated={{ inkBar: true, tabPane: true }}
        tabBarGutter={8}
        destroyInactiveTabPane={false}
        items={[
          {
            key: 'info',
            label: <span><InfoCircleOutlined /> Информация о клубе</span>,
            children: (
              <div className="club-info-container">
                {clubInfo ? (
              <>
                <div className="club-header" style={styles.clubHeader}>
                  <Avatar
                    size={64}
                    src={clubInfo.logo ? (clubInfo.logo.startsWith('http')
                      ? clubInfo.logo
                      : getUploadsBaseUrl() + (clubInfo.logo.startsWith('/') ? clubInfo.logo : '/' + clubInfo.logo)) : undefined}
                    icon={<TrophyOutlined />}
                    style={styles.avatar}
                  />
                  <div style={{ flex: 1 }}>
                    <Title level={3} style={styles.clubTitle}>{clubInfo.name}</Title>
                    <div style={styles.clubSubtitle}>
                      ID: {clubInfo.id} •
                      {clubInfo.status && (
                        <Tag color={clubInfo.status === 'active' ? 'success' : 'error'} style={{ marginLeft: 8 }}>
                          {clubInfo.status === 'active' ? 'Активен' : 'Неактивен'}
                        </Tag>
                      )}
                    </div>
                  </div>
                  <div>
                    <Tooltip title="Редактировать информацию о клубе">
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => setEditClubModalVisible(true)}
                      >
                        Редактировать
                      </Button>
                    </Tooltip>
                  </div>
                </div>

                <Divider orientation="left" style={styles.divider}>Основная информация</Divider>

                <Descriptions bordered column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}>
                  {clubInfo.description && (
                    <Descriptions.Item label="Описание" span={2}>
                      {clubInfo.description}
                    </Descriptions.Item>
                  )}

                  {(clubInfo.federationId || clubInfo.federation?.id) && (
                    <Descriptions.Item label="Федерация">
                      <FederationPopover federationId={clubInfo.federationId || clubInfo.federation?.id}>
                        <span style={{ color: '#1890ff', cursor: 'pointer' }}>
                          {federation ? federation.name : `Федерация ID: ${clubInfo.federationId || clubInfo.federation?.id}`}
                        </span>
                      </FederationPopover>
                    </Descriptions.Item>
                  )}

                  {clubInfo.address && (
                    <Descriptions.Item label={<><EnvironmentOutlined /> Адрес</>}>
                      {clubInfo.address}
                    </Descriptions.Item>
                  )}

                  {clubInfo.phone && (
                    <Descriptions.Item label={<><PhoneOutlined /> Телефон</>}>
                      {clubInfo.phone}
                    </Descriptions.Item>
                  )}

                  {clubInfo.email && (
                    <Descriptions.Item label={<><MailOutlined /> Email</>}>
                      <a href={`mailto:${clubInfo.email}`}>{clubInfo.email}</a>
                    </Descriptions.Item>
                  )}

                  {clubInfo.website && (
                    <Descriptions.Item label={<><GlobalOutlined /> Сайт</>}>
                      <a href={clubInfo.website} target="_blank" rel="noopener noreferrer">
                        {clubInfo.website}
                      </a>
                    </Descriptions.Item>
                  )}
                </Descriptions>

                <Divider orientation="left" style={styles.divider}>Дополнительная информация</Divider>

                <Descriptions bordered column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}>
                  {clubInfo.createdAt && (
                    <Descriptions.Item label={<><CalendarOutlined /> Дата создания</>}>
                      {formatDate(clubInfo.createdAt)}
                    </Descriptions.Item>
                  )}

                  {clubInfo.updatedAt && (
                    <Descriptions.Item label={<><CalendarOutlined /> Дата обновления</>}>
                      {formatDate(clubInfo.updatedAt)}
                    </Descriptions.Item>
                  )}

                  {/* Отображаем остальные поля, которые не обработаны явно */}
                  {Object.entries(clubInfo)
                    .filter(([key]) => ![
                      'id', 'name', 'status', 'description', 'address',
                      'phone', 'email', 'website', 'createdAt', 'updatedAt', 'subscription', 'subscriptionInfo',
                      'logo', 'federationId', 'freeTournamentsCreated'
                    ].includes(key) && typeof clubInfo[key] !== 'object')
                    .map(([key, value]) => {
                      if (!value) return null;

                      // Преобразуем названия полей для отображения
                      const fieldNameMap: Record<string, string> = {
                        // Дополнительные поля, которые могут быть в ответе
                        city: 'Город',
                        region: 'Регион',
                        country: 'Страна',
                        postalCode: 'Почтовый индекс',
                        foundedYear: 'Год основания',
                        memberCount: 'Количество участников',
                        type: 'Тип',
                        category: 'Категория',
                        rating: 'Рейтинг',
                        logo: 'Логотип',
                        socialMedia: 'Социальные сети',
                        contactPerson: 'Контактное лицо',
                        notes: 'Примечания',
                        managerId: 'ID менеджера',
                        federationId: 'ID федерации'
                      };

                      const fieldName = fieldNameMap[key] || key;

                      return (
                        <Descriptions.Item key={key} label={fieldName}>
                          {value}
                        </Descriptions.Item>
                      );
                    })}
                </Descriptions>

                <div style={{ marginTop: '24px', textAlign: 'right' }}>
                  <Button
                    type="primary"
                    onClick={() => fetchParticipants()}
                    icon={<TeamOutlined />}
                  >
                    Обновить данные
                  </Button>
                </div>
              </>
            ) : (
              <Empty
                description="Информация о клубе недоступна"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                styles={{ image: { height: 60 } }}
              >
                <Button type="primary" onClick={fetchUserClub}>Обновить</Button>
              </Empty>
            )}
          </div>
            )
          },
          {
            key: 'participants',
            label: <span><TeamOutlined /> Спортсмены</span>,
            children: (
              <div className="participants-container">
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    <TeamOutlined style={{ color: '#1890ff' }} /> Спортсмены клуба
                  </Title>
                  <div style={{ color: '#8c8c8c', marginTop: 4 }}>
                    {participants.length > 0 ?
                      `Всего спортсменов: ${participants.length}, отфильтровано: ${filteredParticipants.length} ${statusFilter ? (statusFilter === 'active' ? '(активные)' : '(неактивные)') : ''} ${categoryFilter ? `(категория: ${categories.find(c => c.id === categoryFilter)?.name || categoryFilter})` : ''} ${searchText ? `по запросу "${searchText}"` : ''}` :
                      'Список пуст'}
                  </div>
                </div>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => setIsAddModalVisible(true)}
                    disabled={!clubInfo}
                    icon={<TeamOutlined />}
                    size="large"
                  >
                    Добавить спортсмена
                  </Button>
                  <Button
                    type="default"
                    onClick={() => setIsBulkImportModalVisible(true)}
                    disabled={!clubInfo}
                    icon={<UploadOutlined />}
                    size="large"
                  >
                    Массовый импорт
                  </Button>
                </Space>
              </div>

              <div style={{ display: 'flex', marginBottom: 16, background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
                <Space size="middle">
                  <Input
                    placeholder="Поиск по имени"
                    value={searchText}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                    style={{ width: 250 }}
                    allowClear
                    prefix={<SearchOutlined />}
                    size="large"
                  />
                  <Select
                    placeholder="Статус спортсмена"
                    style={{ width: 200 }}
                    onChange={handleStatusFilterChange}
                    allowClear
                    value={statusFilter}
                    size="large"
                  >
                    <Option value="active">Активные спортсмены</Option>
                    <Option value="inactive">Неактивные спортсмены</Option>
                  </Select>

                  <Select
                    placeholder="Категория"
                    style={{ width: 200, marginLeft: 8 }}
                    onChange={handleCategoryFilterChange}
                    allowClear
                    value={categoryFilter}
                    size="large"
                    loading={loadingCategories}
                  >
                    {categories && categories.length > 0 ? (
                      categories.map(category => (
                        <Option key={category.id} value={category.id}>{category.name}</Option>
                      ))
                    ) : (
                      <Option value="" disabled>Нет доступных категорий</Option>
                    )}
                  </Select>
                  {(searchText || statusFilter || categoryFilter) && (
                    <Button
                      onClick={() => {
                        setSearchText('');
                        setStatusFilter(null);
                        setCategoryFilter(null);
                      }}
                      size="large"
                    >
                      Сбросить фильтры
                    </Button>
                  )}
                </Space>
              </div>
            </div>

            {/* Проверяем наличие данных для отображения */}            
            {filteredParticipants.length > 0 ? (
              <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                <Table
                  columns={columns}
                  dataSource={filteredParticipants}
                  loading={loading}
                  rowKey="id"
                  pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Всего ${total} спортсменов`,
                    pageSize: 10,
                    pageSizeOptions: ['10', '20', '50'],
                    locale: {
                      items_per_page: 'спортсменов на странице',
                      jump_to: 'Перейти',
                      jump_to_confirm: 'подтвердить',
                      page: 'Страница',
                      prev_page: 'Предыдущая страница',
                      next_page: 'Следующая страница',
                      prev_5: 'Предыдущие 5 страниц',
                      next_5: 'Следующие 5 страниц',
                      prev_3: 'Предыдущие 3 страницы',
                      next_3: 'Следующие 3 страницы'
                    },
                  }}
                  bordered={false}
                  size="middle"
                  locale={{
                    emptyText: 'Нет данных',
                    filterConfirm: 'ОК',
                    filterReset: 'Сбросить',
                    filterEmptyText: 'Нет фильтров',
                    filterCheckall: 'Выбрать все',
                    filterSearchPlaceholder: 'Поиск в фильтрах',
                    selectAll: 'Выбрать все на текущей странице',
                    selectInvert: 'Инвертировать выбор на текущей странице',
                    selectionAll: 'Выбрать все данные',
                    sortTitle: 'Сортировка',
                    expand: 'Развернуть строку',
                    collapse: 'Свернуть строку',
                    triggerDesc: 'Нажмите для сортировки по убыванию',
                    triggerAsc: 'Нажмите для сортировки по возрастанию',
                    cancelSort: 'Нажмите, чтобы отменить сортировку'
                  }}
                  rowClassName={() => 'participant-row'}
                  style={{ borderRadius: 8 }}
                />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0' }}>
                <Empty
                  description={
                    loading ?
                      'Загрузка списка спортсменов...' :
                      'В клубе пока нет спортсменов'
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  styles={{ image: { height: 80 } }}
                />
                {!loading && (
                  <div style={{ marginTop: 24, textAlign: 'center' }}>
                    <p style={{ color: '#8c8c8c', marginBottom: 16, fontSize: 16 }}>
                      Добавьте спортсменов в клуб, чтобы они отображались в этом списке.
                    </p>
                    <Button
                      type="primary"
                      size="large"
                      icon={<TeamOutlined />}
                      onClick={() => setIsAddModalVisible(true)}
                      disabled={!clubInfo}
                    >
                      Добавить спортсмена
                    </Button>
                  </div>
                )}
              </div>
            )}  {/* Добавлена закрывающая фигурная скобка */}
              </div>
            )
          },
          {
            key: 'subscriptions',
            label: <span><CreditCardOutlined /> {t('club:subscriptions.title')}</span>,
            children: (
              <div className="subscriptions-container">
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<CreditCardOutlined />}
                    onClick={() => navigate('/subscribe')}
                  >
                    {t('club:subscriptions.subscribeButton')}
                  </Button>
                </div>

                {/* Расширенная лицензия — показываем всегда, даже если не приобретена */}
                <Card style={{ marginBottom: 24 }}>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <SafetyCertificateOutlined style={{ fontSize: 22, color: '#1890ff' }} />
                      <Title level={5} style={{ margin: 0 }}>Расширенная лицензия</Title>
                      {extendedLicenseLoading ? (
                        <Typography.Text type="secondary">Загрузка...</Typography.Text>
                      ) : extendedLicenseInfo?.hasExtendedLicense ? (
                        <Tag color="success">Оформлена</Tag>
                      ) : (
                        <Tag color="default">Не оформлена</Tag>
                      )}
                    </div>
                    <Text type="secondary" style={{ display: 'block' }}>
                      Даёт возможность создавать турниры от имени клуба. Без расширенной лицензии создание турниров недоступно.
                    </Text>
                    {!extendedLicenseLoading && extendedLicenseInfo && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                        {extendedLicenseInfo.hasExtendedLicense ? (
                          <Button type="link" onClick={() => navigate('/extended-license')} style={{ padding: 0 }}>
                            Подробнее
                          </Button>
                        ) : (
                          <>
                            {extendedLicenseInfo.extendedLicensePrice > 0 && (
                              <Text strong>{extendedLicenseInfo.extendedLicensePrice} ₽</Text>
                            )}
                            <Button
                              type="primary"
                              size="small"
                              onClick={() => navigate('/extended-license')}
                            >
                              {extendedLicenseInfo.extendedLicensePrice > 0
                                ? 'Оформить расширенную лицензию'
                                : 'Перейти к расширенной лицензии'}
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </Space>
                </Card>

                {loadingSubscriptions ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Empty
                      description={t('club:subscriptions.loading')}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      styles={{ image: { height: 60 } }}
                    />
                  </div>
                ) : subscriptions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Empty
                      description={t('club:subscriptions.noSubscriptions')}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      styles={{ image: { height: 60 } }}
                    />
                    <Button
                      type="primary"
                      style={{ marginTop: 16 }}
                      icon={<CreditCardOutlined />}
                      onClick={() => navigate('/subscribe')}
                    >
                      {t('club:subscriptions.subscribeButton')}
                    </Button>
                  </div>
                ) : (
                  <>
                    {(() => {
                      // Находим все активные подписки
                      const activeSubscriptions = subscriptions.filter((sub: any) => 
                        sub !== null && 
                        sub !== undefined && 
                        sub.status === 'active'
                      );

                      if (activeSubscriptions.length === 0) {
                        return (
                          <Card>
                            <Alert
                              message={t('club:subscriptions.noActiveSubscriptions')}
                              description={t('club:subscriptions.noActiveSubscriptionsDescription')}
                              type="warning"
                              icon={<CloseCircleOutlined />}
                              style={{ marginBottom: 16 }}
                              action={
                                <Button 
                                  type="primary" 
                                  icon={<CreditCardOutlined />}
                                  onClick={() => navigate('/subscribe')}
                                >
                                  {t('club:subscriptions.subscribeButton')}
                                </Button>
                              }
                            />
                          </Card>
                        );
                      }

                      let totalMaxParticipants = 0;
                      let totalUsedParticipants = 0;
                      let hasUnlimitedParticipants = false;
                      activeSubscriptions.forEach((sub: any) => {
                        const plan = sub.subscriptionPlan;
                        const maxP = sub.maxParticipantsOverride ?? plan?.maxParticipants;
                        const numMaxP = maxP != null ? Number(maxP) : NaN;
                        if (maxP === null || maxP === undefined) hasUnlimitedParticipants = true;
                        else if (!hasUnlimitedParticipants && !Number.isNaN(numMaxP)) totalMaxParticipants += numMaxP;
                        totalUsedParticipants += Number(sub.usedParticipants) || 0;
                      });
                      const safeMaxP = Number.isFinite(totalMaxParticipants) ? totalMaxParticipants : 0;
                      const showUnlimitedP = hasUnlimitedParticipants || !Number.isFinite(totalMaxParticipants);
                      const remainingParticipants = showUnlimitedP ? null : Math.max(0, safeMaxP - totalUsedParticipants);

                      return (
                        <>
                          <Card style={{ marginBottom: 16, borderRadius: 12, overflow: 'hidden' }}>
                            <div style={{ marginBottom: 24 }}>
                              <Title level={4} style={{ marginBottom: 4 }}>
                                {activeSubscriptions.length > 1 
                                  ? t('club:subscriptions.activeTitlePlural', { count: activeSubscriptions.length })
                                  : t('club:subscriptions.activeTitle')}
                              </Title>
                              <Text type="secondary">{t('club:subscriptions.limitsHint', 'Лимиты списываются с клуба организатора при проведении турниров')}</Text>
                            </div>

                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                              <Col xs={24}>
                                <Card size="small" style={{ background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)', border: '1px solid #91d5ff', borderRadius: 8 }}>
                                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text strong style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <TeamOutlined /> {t('club:subscriptions.participants', 'Участники')}
                                    </Text>
                                    <div>
                                      <Text>
                                        {t('club:subscriptions.used')}: <strong>{totalUsedParticipants}</strong>
                                        {showUnlimitedP ? ` / ${t('club:subscriptions.unlimited')}` : ` ${t('club:subscriptions.of', 'из')} ${safeMaxP}`}
                                        {remainingParticipants !== null && (
                                          <> · {t('club:subscriptions.remaining')}: <strong>{remainingParticipants}</strong></>
                                        )}
                                      </Text>
                                      {!showUnlimitedP && safeMaxP > 0 && (
                                        <Progress
                                          percent={Math.min(100, Math.round((totalUsedParticipants / safeMaxP) * 100))}
                                          size="small"
                                          showInfo={false}
                                          strokeColor="#1890ff"
                                          style={{ marginTop: 6 }}
                                        />
                                      )}
                                    </div>
                                  </Space>
                                </Card>
                              </Col>
                            </Row>

                            {activeSubscriptions.map((subscription: any, index: number) => {
                              const plan = subscription.subscriptionPlan;
                              const maxP = subscription.maxParticipantsOverride ?? plan?.maxParticipants ?? null;
                              const usedP = subscription.usedParticipants ?? 0;
                              const percentP = maxP != null && maxP > 0 ? Math.min(100, (usedP / maxP) * 100) : 0;
                              return (
                                <Card
                                  key={subscription.id || index}
                                  size="small"
                                  style={{
                                    marginBottom: index < activeSubscriptions.length - 1 ? 16 : 0,
                                    borderLeft: '4px solid #1890ff',
                                    borderRadius: 8
                                  }}
                                  title={activeSubscriptions.length > 1 ? `${t('club:subscriptions.subscriptionNumber', { number: index + 1 })}: ${plan?.name || t('common:labels.name')}` : (plan?.name || t('club:subscriptions.plan'))}
                                  extra={subscription.endDate ? <Text type="secondary"><CalendarOutlined /> {t('club:subscriptions.until', 'до')} {formatDate(subscription.endDate)}</Text> : null}
                                >
                                  <Row gutter={[16, 12]}>
                                    <Col span={24}>
                                      <Text type="secondary">{t('club:subscriptions.startDate')}: {formatDate(subscription.startDate)}</Text>
                                      {subscription.price != null && subscription.price > 0 && (
                                        <> · <Text type="secondary">{parseFloat(String(subscription.price)).toFixed(0)} ₽</Text></>
                                      )}
                                    </Col>
                                    <Col span={24}>
                                      <div style={{ marginBottom: 4 }}>
                                        <Text strong>{t('club:subscriptions.participants', 'Участники')}:</Text>{' '}
                                        {usedP}{maxP != null ? ` / ${maxP}` : ` / ∞`}
                                      </div>
                                      {maxP != null && maxP > 0 && (
                                        <Progress percent={Math.round(percentP)} size="small" strokeColor="#1890ff" />
                                      )}
                                    </Col>
                                    {plan?.features && Array.isArray(plan.features) && plan.features.length > 0 && (
                                      <Col span={24}>
                                        <Text type="secondary">{t('club:subscriptions.features')}: </Text>
                                        {plan.features.slice(0, 3).map((f: string, i: number) => (
                                          <Tag key={i} style={{ marginTop: 4 }}>{String(f)}</Tag>
                                        ))}
                                        {plan.features.length > 3 && <Tag>+{plan.features.length - 3}</Tag>}
                                      </Col>
                                    )}
                                  </Row>
                                </Card>
                              );
                            })}
                          </Card>

                          {subscriptions.length > 0 && (
                            <Card>
                              <Title level={4} style={{ marginBottom: 16 }}>{t('club:subscriptions.history')}</Title>
                              <Table
                                columns={[
                                  {
                                    title: t('club:subscriptions.table.columns.plan'),
                                    key: 'subscriptionPlan',
                                    render: (_: unknown, record: any) => {
                                      if (!record || !record.subscriptionPlan) {
                                        return t('common:labels.name');
                                      }
                                      return record.subscriptionPlan.name || t('common:labels.name');
                                    },
                                  },
                                  {
                                    title: t('common:labels.status'),
                                    dataIndex: 'status',
                                    key: 'status',
                                    render: (status: string) => {
                                      const statusConfig: Record<string, { color: string; text: string }> = {
                                        active: { color: 'green', text: t('subscription:status.active') },
                                        cancelled: { color: 'red', text: t('subscription:status.cancelled') },
                                        expired: { color: 'orange', text: t('subscription:status.expired') },
                                        pending: { color: 'blue', text: t('subscription:status.pending') }
                                      };
                                      const config = statusConfig[status] || { color: 'default', text: status };
                                      return <Tag color={config.color}>{config.text}</Tag>;
                                    },
                                  },
                                  {
                                    title: t('club:subscriptions.table.columns.startDate'),
                                    dataIndex: 'startDate',
                                    key: 'startDate',
                                    render: (date: string) => date ? formatDate(date) : '-',
                                  },
                                  {
                                    title: t('club:subscriptions.table.columns.endDate'),
                                    dataIndex: 'endDate',
                                    key: 'endDate',
                                    render: (date: string | null) => date ? formatDate(date) : t('club:subscriptions.table.columns.noEndDate'),
                                  },
                                  {
                                    title: t('club:subscriptions.table.columns.price'),
                                    dataIndex: 'price',
                                    key: 'price',
                                    render: (price: number | null | undefined) => {
                                      const priceValue = price ?? 0;
                                      return `${parseFloat(priceValue.toString()).toFixed(2)} ₽`;
                                    },
                                  },
                                ]}
                                dataSource={subscriptions}
                                rowKey={(record) => record.id?.toString() || String(Math.random())}
                                loading={loadingSubscriptions}
                                pagination={{
                                  showSizeChanger: true,
                                  showTotal: (total) => `${t('common:pagination.total')} ${total} ${t('club:subscriptions.historyCount', { count: total })}`,
                                }}
                              />
                            </Card>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            )
          },
          {
            key: 'coaches',
            label: <span><TeamOutlined /> Тренеры</span>,
            children: (
              <div className="coaches-container">
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        <UserOutlined style={{ color: '#1890ff' }} /> Тренеры клуба
                      </Title>
                      <div style={{ color: '#8c8c8c', marginTop: 4 }}>
                        {coachesList.length > 0 ?
                          `Всего тренеров: ${coachesList.length}, отфильтровано: ${filteredCoaches.length} ${coachStatusFilter ? (coachStatusFilter === 'active' ? '(активные)' : '(неактивные)') : ''} ${coachSearchText ? `по запросу "${coachSearchText}"` : ''}` :
                          'Список пуст'}
                      </div>
                    </div>
                    <Button
                      type="primary"
                      onClick={() => setIsAddCoachModalVisible(true)}
                      disabled={!clubInfo}
                      icon={<UserOutlined />}
                      size="large"
                    >
                      Добавить тренера
                    </Button>
                  </div>

                  <div style={{ display: 'flex', marginBottom: 16, background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
                    <Space size="middle">
                      <Input
                        placeholder="Поиск по имени"
                        value={coachSearchText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCoachSearchText(e.target.value)}
                        style={{ width: 250 }}
                        allowClear
                        prefix={<SearchOutlined />}
                        size="large"
                      />
                      <Select
                        placeholder="Статус тренера"
                        style={{ width: 200 }}
                        onChange={handleCoachStatusFilterChange}
                        allowClear
                        value={coachStatusFilter}
                        size="large"
                      >
                        <Option value="active">Активные тренеры</Option>
                        <Option value="inactive">Неактивные тренеры</Option>
                      </Select>
                      {(coachSearchText || coachStatusFilter) && (
                        <Button
                          onClick={() => {
                            setCoachSearchText('');
                            setCoachStatusFilter(null);
                          }}
                          size="large"
                        >
                          Сбросить фильтры
                        </Button>
                      )}
                    </Space>
                  </div>
                </div>

                {filteredCoaches.length > 0 ? (
                  <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                    <Table
                      columns={coachColumns}
                      dataSource={filteredCoaches}
                      loading={loadingCoachesList}
                      rowKey="id"
                      pagination={{
                        showSizeChanger: true,
                        showTotal: (total) => `Всего ${total} тренеров`,
                        pageSize: 10,
                        pageSizeOptions: ['10', '20', '50'],
                        locale: {
                          items_per_page: 'тренеров на странице',
                          jump_to: 'Перейти',
                          jump_to_confirm: 'подтвердить',
                          page: 'Страница',
                          prev_page: 'Предыдущая страница',
                          next_page: 'Следующая страница',
                          prev_5: 'Предыдущие 5 страниц',
                          next_5: 'Следующие 5 страниц',
                          prev_3: 'Предыдущие 3 страницы',
                          next_3: 'Следующие 3 страницы'
                        },
                      }}
                      bordered={false}
                      size="middle"
                      locale={{
                        emptyText: 'Нет данных',
                        filterConfirm: 'ОК',
                        filterReset: 'Сбросить',
                        filterEmptyText: 'Нет фильтров',
                        filterCheckall: 'Выбрать все',
                        filterSearchPlaceholder: 'Поиск в фильтрах',
                        selectAll: 'Выбрать все на текущей странице',
                        selectInvert: 'Инвертировать выбор на текущей странице',
                        selectionAll: 'Выбрать все данные',
                        sortTitle: 'Сортировка',
                        expand: 'Развернуть строку',
                        collapse: 'Свернуть строку',
                        triggerDesc: 'Нажмите для сортировки по убыванию',
                        triggerAsc: 'Нажмите для сортировки по возрастанию',
                        cancelSort: 'Нажмите, чтобы отменить сортировку'
                      }}
                      rowClassName={() => 'coach-row'}
                      style={{ borderRadius: 8 }}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0' }}>
                    <Empty
                      description={
                        loadingCoachesList ?
                          'Загрузка списка тренеров...' :
                          'В клубе пока нет тренеров'
                      }
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      styles={{ image: { height: 80 } }}
                    />
                    {!loadingCoachesList && (
                      <div style={{ marginTop: 24, textAlign: 'center' }}>
                        <p style={{ color: '#8c8c8c', marginBottom: 16, fontSize: 16 }}>
                          Добавьте тренеров в клуб, чтобы они отображались в этом списке.
                        </p>
                        <Button
                          type="primary"
                          size="large"
                          icon={<UserOutlined />}
                          onClick={() => setIsAddCoachModalVisible(true)}
                          disabled={!clubInfo}
                        >
                          Добавить тренера
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          },
          {
            key: 'managers',
            label: <span><UsergroupAddOutlined /> Менеджеры</span>,
            children: (
              <div className="managers-container" style={{ padding: '16px 0' }}>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        <UsergroupAddOutlined style={{ color: '#1890ff' }} /> Менеджеры клуба
                      </Title>
                      <div style={{ color: '#8c8c8c', marginTop: 4 }}>
                        {clubManagers.length > 0 ? `Всего менеджеров: ${clubManagers.length}` : 'Список пуст'}
                      </div>
                    </div>
                    {canInviteManager && (
                      <Button
                        type="primary"
                        onClick={() => setIsInviteManagerModalVisible(true)}
                        disabled={!clubInfo}
                        icon={<UserAddOutlined />}
                        size="large"
                      >
                        Пригласить менеджера
                      </Button>
                    )}
                  </div>
                </div>
                {clubManagersLoading ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>
                ) : clubManagers.length === 0 ? (
                  <Empty
                    description="В клубе пока нет менеджеров"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: 40 }}
                  >
                    {canInviteManager && (
                      <Button
                        type="primary"
                        size="large"
                        icon={<UserAddOutlined />}
                        onClick={() => setIsInviteManagerModalVisible(true)}
                        disabled={!clubInfo}
                      >
                        Пригласить менеджера
                      </Button>
                    )}
                  </Empty>
                ) : (
                  <Table
                    size="small"
                    dataSource={clubManagers}
                    rowKey="id"
                    columns={[
                      {
                        title: 'ФИО',
                        key: 'fullName',
                        render: (_: unknown, r: any) => `${r.lastName || ''} ${r.firstName || ''}${r.middleName ? ` ${r.middleName}` : ''}`.trim(),
                      },
                      { title: 'Email', dataIndex: 'email', key: 'email' },
                      {
                        title: 'Статус',
                        dataIndex: 'status',
                        key: 'status',
                        render: (s: string) => <Tag color={s === 'active' ? 'green' : 'default'}>{s === 'active' ? 'Активен' : s || '—'}</Tag>,
                      },
                    ]}
                    pagination={false}
                  />
                )}
              </div>
            )
          },
          {
            key: 'invitations',
            label: (
              <span>
                <UserAddOutlined /> Приглашения менеджеров
                {pendingUsers.length > 0 && (
                  <Badge count={pendingUsers.length} style={{ marginLeft: 8 }} title="Ожидают подтверждения" />
                )}
              </span>
            ),
            children: (
              <div className="invitations-container" style={{ padding: '16px 0' }}>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={4} style={{ margin: 0 }}>
                      <UserAddOutlined style={{ color: '#1890ff' }} /> Приглашения в менеджеры клуба
                    </Title>
                    {canInviteManager && (
                      <Button
                        type="primary"
                        onClick={() => setIsInviteManagerModalVisible(true)}
                        disabled={!clubInfo}
                        icon={<UserAddOutlined />}
                        size="large"
                      >
                        Пригласить по email
                      </Button>
                    )}
                  </div>
                  <Alert
                    message="Информация"
                    description="Пригласите новых менеджеров клуба по email. Они получат письмо со ссылкой для принятия приглашения."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  {pendingUsers.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                      <Title level={5}>Ожидающие подтверждения</Title>
                      <Alert
                        message="Регистрация в клуб"
                        description="Эти пользователи зарегистрировались в клуб и ожидают подтверждения. Подтвердите их как менеджеров для предоставления доступа к данным клуба."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12 }}
                      />
                      <Table
                        size="small"
                        dataSource={pendingUsers}
                        rowKey="id"
                        loading={pendingUsersLoading}
                        columns={[
                          { title: 'Email', dataIndex: 'email', key: 'email' },
                          { title: 'Имя', key: 'name', render: (_: unknown, r: any) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || '-' },
                          { title: 'Регистрация', dataIndex: 'createdAt', key: 'createdAt', render: (d: string) => d ? new Date(d).toLocaleString('ru-RU') : '-' },
                          {
                            title: 'Действия',
                            key: 'actions',
                            render: (_: unknown, record: any) =>
                              canInviteManager ? (
                                <Button type="primary" size="small" icon={<CheckCircleOutlined />} onClick={() => clubInfo?.id && handleApprovePending(record.id, clubInfo.id)}>
                                  Подтвердить
                                </Button>
                              ) : null,
                          },
                        ]}
                        pagination={false}
                      />
                    </div>
                  )}
                </div>
                {invitationsLoading ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>
                ) : invitations.length === 0 ? (
                  <Empty
                    description="Нет приглашений"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: 40 }}
                  />
                ) : (
                  <Table
                    size="small"
                    dataSource={invitations}
                    rowKey="id"
                    columns={[
                      { title: 'Email', dataIndex: 'email', key: 'email' },
                      { title: 'Роль', dataIndex: 'role', key: 'role', render: (r: string) => r === 'head_manager' ? 'Главный менеджер' : 'Менеджер' },
                      { title: 'Статус', dataIndex: 'status', key: 'status', render: (s: string) => <Tag color={s === 'pending' ? 'blue' : s === 'accepted' ? 'green' : 'default'}>{s === 'pending' ? 'Ожидает' : s === 'accepted' ? 'Принято' : s}</Tag> },
                      { title: 'Срок', dataIndex: 'expiresAt', key: 'expiresAt', render: (d: string) => d ? new Date(d).toLocaleString('ru-RU') : '-' },
                      {
                        title: 'Действия',
                        key: 'actions',
                        render: (_: unknown, record: any) =>
                          record.status === 'pending' && canInviteManager ? (
                            <Button type="link" danger size="small" onClick={() => handleCancelInvitation(record.id, record.clubId)}>
                              Отменить
                            </Button>
                          ) : null,
                      },
                    ]}
                    pagination={false}
                  />
                )}
              </div>
            )
          }
        ]}
      />
      )}

      {clubInfo && (
        <>
          <AddParticipantModal
            visible={isAddModalVisible}
            clubId={clubInfo.id}
            clubName={clubInfo.name}
            onCancel={() => setIsAddModalVisible(false)}
            onSuccess={() => {
              setIsAddModalVisible(false);
              fetchParticipants();
              // Обновляем информацию о тренерах после добавления нового участника
              fetchCoaches();
            }}
          />
          <EditParticipantModal
            visible={editParticipantModalVisible}
            participant={selectedParticipant?.participant || null}
            clubId={clubInfo?.id} // Передаем ID клуба для фильтрации тренеров
            onCancel={() => {
              setEditParticipantModalVisible(false);
              setSelectedParticipant(null);
            }}
            onSuccess={() => {
              setEditParticipantModalVisible(false);
              setSelectedParticipant(null);
              fetchParticipants();
              // Обновляем информацию о тренерах после редактирования участника
              fetchCoaches();
            }}
          />

          {/* Модальное окно редактирования данных клуба */}
          <EditClubModal
            visible={editClubModalVisible}
            club={clubInfo as any}
            onCancel={() => setEditClubModalVisible(false)}
            onSuccess={() => {
              setEditClubModalVisible(false);
              // Перезагружаем информацию о клубе
              handleClubChange(clubInfo.id);
            }}
          />

          {/* Модальное окно добавления тренера */}
          <CreateCoachModal
            visible={isAddCoachModalVisible}
            coach={null}
            clubId={clubInfo?.id}
            onCancel={() => setIsAddCoachModalVisible(false)}
            onSuccess={() => {
              setIsAddCoachModalVisible(false);
              fetchCoachesList();
              fetchCoaches(); // Обновляем также список для участников
            }}
          />

          {/* Модальное окно редактирования тренера */}
          <CreateCoachModal
            visible={isEditCoachModalVisible}
            coach={selectedCoach}
            clubId={clubInfo?.id}
            onCancel={() => {
              setIsEditCoachModalVisible(false);
              setSelectedCoach(null);
            }}
            onSuccess={() => {
              setIsEditCoachModalVisible(false);
              setSelectedCoach(null);
              fetchCoachesList();
              fetchCoaches(); // Обновляем также список для участников
            }}
          />

          {/* Модальное окно статистики спортсмена */}
          <Modal
            title="Статистика спортсмена"
            open={isStatisticsModalVisible}
            onCancel={() => {
              setIsStatisticsModalVisible(false);
              setSelectedParticipantId(null);
            }}
            footer={null}
            width={800}
            destroyOnClose
          >
            {selectedParticipantId && (
              <ParticipantStatistics participantId={selectedParticipantId} />
            )}
          </Modal>

          {/* Модальное окно массового импорта */}
          <BulkImportModal
            visible={isBulkImportModalVisible}
            clubId={clubInfo?.id || 0}
            onCancel={() => setIsBulkImportModalVisible(false)}
            onSuccess={() => {
              setIsBulkImportModalVisible(false);
              fetchParticipants();
            }}
          />

          {/* Модальное окно приглашения менеджера */}
          <InviteManagerModal
            visible={isInviteManagerModalVisible}
            clubs={clubInfo ? [{ id: clubInfo.id, name: clubInfo.name, status: (clubInfo.status || 'active') as 'active' | 'inactive' }] : []}
            initialClubId={clubInfo?.id}
            onCancel={() => setIsInviteManagerModalVisible(false)}
            onSuccess={() => {
              setIsInviteManagerModalVisible(false);
              if (clubInfo?.id) fetchInvitations(clubInfo.id);
            }}
          />
        </>
      )}
    </Card>
  );
};

export default ClubManagement;
